// RUNN — compliance UI: age gate + consent banner + preferences modal.
// Self-contained (injects its own styles in the RUNN design system).
// Landing calls initCompliance(); the app calls initCompliance({ requireAge:true }).
import {
  CATEGORIES, CATEGORY_COPY, getConsent, needsConsent, defaultConsent,
  acceptAll, rejectNonEssential, setConsent,
} from './consent.js';
import { currentFramework, APP_MIN_AGE } from './regions.js';
import { isAgeVerified, recordAge, evaluateDOB } from './age-gate.js';

let stylesInjected = false;

function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const css = `
  .runn-cmp, .runn-cmp *{box-sizing:border-box}
  .runn-cmp{--void:#0A0B14;--surface:#13172A;--surface2:#1B2138;--line:#262C45;
    --chalk:#F3F2EC;--smoke:#9197AE;--smoke-dim:#666C84;--punch:#FF2E83;--cyan:#2BE6CA;--lime:#B9FF3D;
    font-family:"Space Grotesk",system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:var(--chalk)}
  .runn-overlay{position:fixed;inset:0;z-index:9000;background:rgba(5,6,12,.78);
    backdrop-filter:blur(8px);display:grid;place-items:center;padding:22px;animation:runn-fade .25s ease}
  @keyframes runn-fade{from{opacity:0}to{opacity:1}}
  .runn-card{background:linear-gradient(180deg,var(--surface2),var(--surface));
    border:1px solid var(--line);border-radius:20px;max-width:480px;width:100%;
    padding:30px 28px;box-shadow:0 30px 80px -20px rgba(0,0,0,.7)}
  .runn-card h2{font-size:1.5rem;line-height:1.1;letter-spacing:-.02em;font-weight:800;margin:0 0 8px}
  .runn-card p{color:var(--smoke);font-size:.96rem;line-height:1.55;margin:0 0 18px}
  .runn-pin{width:46px;height:46px;display:block;margin-bottom:16px}
  .runn-nn{color:var(--punch)}
  .runn-dob{display:flex;gap:10px;margin:0 0 18px}
  .runn-dob select{flex:1;background:var(--void);border:1px solid var(--line);color:var(--chalk);
    border-radius:12px;padding:13px 12px;font:inherit;font-size:.95rem;appearance:none;cursor:pointer}
  .runn-dob select:focus{outline:none;border-color:var(--punch)}
  .runn-err{color:#FF6B6B;font-size:.85rem;font-family:"Space Mono",monospace;margin:-8px 0 16px;min-height:1em}
  .runn-row{display:flex;gap:12px;flex-wrap:wrap}
  .runn-btn{flex:1;min-width:130px;font:inherit;font-weight:700;font-size:.95rem;cursor:pointer;
    padding:14px 20px;border-radius:999px;border:1px solid transparent;transition:transform .2s ease,background .2s,border-color .2s;white-space:nowrap}
  .runn-btn:hover{transform:translateY(-2px)}
  .runn-btn-primary{background:var(--punch);color:var(--void);box-shadow:0 8px 26px -8px var(--punch)}
  .runn-btn-ghost{background:transparent;color:var(--chalk);border-color:var(--line)}
  .runn-btn-ghost:hover{border-color:var(--chalk)}
  .runn-fineprint{font-size:.78rem;color:var(--smoke-dim);line-height:1.5;margin-top:16px}
  .runn-fineprint a{color:var(--cyan);text-decoration:underline;text-underline-offset:2px}
  /* banner */
  .runn-banner{position:fixed;left:0;right:0;bottom:0;z-index:8500;padding:16px;animation:runn-slide .35s cubic-bezier(.2,.7,.2,1)}
  @keyframes runn-slide{from{transform:translateY(110%)}to{transform:translateY(0)}}
  .runn-banner-inner{max-width:1080px;margin:0 auto;background:linear-gradient(180deg,var(--surface2),var(--surface));
    border:1px solid var(--line);border-radius:18px;padding:20px 22px;display:grid;grid-template-columns:1fr auto;
    gap:18px;align-items:center;box-shadow:0 24px 60px -22px rgba(0,0,0,.7)}
  .runn-banner-inner p{margin:0;color:var(--smoke);font-size:.9rem;line-height:1.55}
  .runn-banner-inner b{color:var(--chalk)}
  .runn-banner-actions{display:flex;gap:10px;flex-wrap:wrap;justify-content:flex-end}
  .runn-banner-actions .runn-btn{flex:none;min-width:0;padding:12px 18px;font-size:.88rem}
  .runn-region{font-family:"Space Mono",monospace;font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--smoke-dim);display:block;margin-bottom:6px}
  /* preferences toggles */
  .runn-cat{display:flex;gap:14px;align-items:flex-start;padding:14px 0;border-top:1px solid var(--line)}
  .runn-cat:first-of-type{border-top:0}
  .runn-cat .meta{flex:1}
  .runn-cat .nm{font-weight:700;font-size:.95rem;margin-bottom:3px}
  .runn-cat .ds{color:var(--smoke);font-size:.82rem;line-height:1.45}
  .runn-prefs-scroll{max-height:46vh;overflow:auto;margin:6px -4px 18px;padding:0 4px}
  .runn-sw{position:relative;width:46px;height:27px;flex:none;margin-top:2px}
  .runn-sw input{opacity:0;width:100%;height:100%;margin:0;cursor:pointer}
  .runn-sw .track{position:absolute;inset:0;border-radius:999px;background:var(--line);transition:background .2s}
  .runn-sw .knob{position:absolute;top:3px;left:3px;width:21px;height:21px;border-radius:50%;background:var(--chalk);transition:transform .2s}
  .runn-sw input:checked + .track{background:var(--punch)}
  .runn-sw input:checked + .track + .knob{transform:translateX(19px)}
  .runn-sw input:disabled{cursor:not-allowed}
  .runn-sw input:disabled + .track{background:var(--cyan);opacity:.4}
  @media (max-width:640px){.runn-banner-inner{grid-template-columns:1fr}.runn-banner-actions{justify-content:stretch}.runn-banner-actions .runn-btn{flex:1}}
  `;
  const tag = document.createElement('style');
  tag.id = 'runn-cmp-styles';
  tag.textContent = css;
  document.head.appendChild(tag);
}

const PIN_SVG = `<svg class="runn-pin" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 396C200 320 168 280 168 224a88 88 0 1 1 176 0c0 56-32 96-88 172Z" fill="#FF2E83"/><circle cx="256" cy="212" r="40" fill="#0A0B14"/></svg>`;

function el(html) {
  const t = document.createElement('div');
  t.innerHTML = html.trim();
  return t.firstElementChild;
}

/* ---------------- Age gate (blocking) ---------------- */
function showAgeGate() {
  return new Promise((resolve) => {
    injectStyles();
    const months = ['Month','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const thisYear = new Date().getFullYear();
    const days = ['<option value="">Day</option>'].concat(
      Array.from({ length: 31 }, (_, i) => `<option>${i + 1}</option>`)
    ).join('');
    const mopts = months.map((m, i) => `<option value="${i === 0 ? '' : i}">${m}</option>`).join('');
    const years = ['<option value="">Year</option>'].concat(
      Array.from({ length: 100 }, (_, i) => `<option>${thisYear - i}</option>`)
    ).join('');

    const overlay = el(`
      <div class="runn-cmp"><div class="runn-overlay" role="dialog" aria-modal="true" aria-label="Age check">
        <div class="runn-card">
          ${PIN_SVG}
          <h2>First — how old are you<span class="runn-nn">?</span></h2>
          <p>RUNN tracks real-world location and movement, so we keep it <b>${APP_MIN_AGE}+</b>. Enter your date of birth — we only store whether you passed, never the date itself.</p>
          <div class="runn-dob">
            <select aria-label="Day" data-f="d">${days}</select>
            <select aria-label="Month" data-f="m">${mopts}</select>
            <select aria-label="Year" data-f="y">${years}</select>
          </div>
          <div class="runn-err" data-err></div>
          <div class="runn-row"><button class="runn-btn runn-btn-primary" data-go>Enter RUNN</button></div>
          <div class="runn-fineprint">By continuing you agree to our <a href="/legal/terms.html">Terms</a> and <a href="/legal/privacy.html">Privacy Policy</a>.</div>
        </div>
      </div></div>`);
    document.body.appendChild(overlay);

    const get = (f) => overlay.querySelector(`[data-f="${f}"]`).value;
    const errEl = overlay.querySelector('[data-err]');
    overlay.querySelector('[data-go]').addEventListener('click', () => {
      const res = evaluateDOB(get('y'), get('m'), get('d'));
      if (res.reason === 'incomplete') { errEl.textContent = 'Please pick your full date of birth.'; return; }
      if (res.reason === 'invalid-date') { errEl.textContent = "That date doesn't look right."; return; }
      recordAge(res.ok, res.age);
      if (res.ok) {
        overlay.remove();
        resolve(true);
      } else {
        // Graceful, non-judgemental block. No further data collected.
        overlay.querySelector('.runn-card').innerHTML = `
          ${PIN_SVG}
          <h2>Catch you in a bit<span class="runn-nn">.</span></h2>
          <p>RUNN is built for players aged ${APP_MIN_AGE} and up. Thanks for stopping by — the city will still be here when you're ready.</p>
          <div class="runn-row"><a class="runn-btn runn-btn-ghost" href="https://www.google.com" style="text-align:center;text-decoration:none;line-height:1.2">Leave</a></div>`;
        resolve(false);
      }
    });
  });
}

/* ---------------- Consent banner ---------------- */
function showBanner(api) {
  injectStyles();
  const fw = currentFramework();
  const banner = el(`
    <div class="runn-cmp"><div class="runn-banner" role="region" aria-label="Privacy choices">
      <div class="runn-banner-inner">
        <div>
          <span class="runn-region">Your region · ${fw.label}</span>
          <p>We use cookies and similar tech for core gameplay, and — only with your say-so — for <b>location</b>, <b>health</b>, analytics and marketing. You're in control. See our <a href="/legal/cookies.html" style="color:var(--cyan)">Cookie Policy</a>.</p>
        </div>
        <div class="runn-banner-actions">
          <button class="runn-btn runn-btn-ghost" data-act="customize">Customize</button>
          <button class="runn-btn runn-btn-ghost" data-act="reject">Reject non-essential</button>
          <button class="runn-btn runn-btn-primary" data-act="accept">Accept all</button>
        </div>
      </div>
    </div></div>`);
  document.body.appendChild(banner);

  banner.querySelector('[data-act="accept"]').addEventListener('click', () => { acceptAll(); banner.remove(); });
  banner.querySelector('[data-act="reject"]').addEventListener('click', () => { rejectNonEssential(); banner.remove(); });
  banner.querySelector('[data-act="customize"]').addEventListener('click', () => { banner.remove(); api.openPreferences(); });
}

/* ---------------- Preferences modal ---------------- */
function openPreferences() {
  injectStyles();
  const fw = currentFramework();
  const existing = getConsent();
  const start = existing ? existing.categories : defaultConsent();

  const rows = CATEGORIES.map((k) => {
    const c = CATEGORY_COPY[k];
    const on = !!start[k];
    const locked = !!c.locked;
    return `<div class="runn-cat">
      <div class="meta"><div class="nm">${c.name}</div><div class="ds">${c.desc}</div></div>
      <label class="runn-sw">
        <input type="checkbox" data-cat="${k}" ${on ? 'checked' : ''} ${locked ? 'disabled' : ''}/>
        <span class="track"></span><span class="knob"></span>
      </label>
    </div>`;
  }).join('');

  const overlay = el(`
    <div class="runn-cmp"><div class="runn-overlay" role="dialog" aria-modal="true" aria-label="Privacy preferences">
      <div class="runn-card">
        <h2>Your privacy choices</h2>
        <p>Toggle exactly what RUNN may use. <span class="runn-region" style="display:inline">${fw.label}</span></p>
        <div class="runn-prefs-scroll">${rows}</div>
        <div class="runn-row">
          <button class="runn-btn runn-btn-ghost" data-act="reject">Reject non-essential</button>
          <button class="runn-btn runn-btn-primary" data-act="save">Save choices</button>
        </div>
        <div class="runn-fineprint">Read our full <a href="/legal/privacy.html">Privacy Policy</a>. You can change or withdraw consent anytime from this panel.</div>
      </div>
    </div></div>`);
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target.classList.contains('runn-overlay')) close(); });
  document.addEventListener('keydown', function esc(e) { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', esc); } });

  overlay.querySelector('[data-act="save"]').addEventListener('click', () => {
    const picked = {};
    CATEGORIES.forEach((k) => {
      const box = overlay.querySelector(`[data-cat="${k}"]`);
      picked[k] = box ? box.checked : false;
    });
    setConsent(picked, 'preferences');
    close();
  });
  overlay.querySelector('[data-act="reject"]').addEventListener('click', () => { rejectNonEssential(); close(); });
}

/**
 * Entry point.
 * @param {{requireAge?:boolean, onReady?:Function}} opts
 * @returns {{openPreferences:Function}}
 */
export function initCompliance(opts = {}) {
  injectStyles();
  const api = { openPreferences };

  const run = () => {
    const startConsentFlow = () => {
      if (needsConsent()) showBanner(api);
      if (typeof opts.onReady === 'function') opts.onReady(true);
    };

    if (opts.requireAge && !isAgeVerified()) {
      showAgeGate().then((passed) => {
        if (passed) startConsentFlow();
        else if (typeof opts.onReady === 'function') opts.onReady(false);
      });
    } else {
      startConsentFlow();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
  return api;
}

export { openPreferences };
